import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { WorkflowPhaseManager } from "@/components/tasks/WorkflowPhaseManager";
import { WorkflowGuidancePanel } from "@/components/tasks/WorkflowGuidancePanel";
import { DocumentsList } from "@/components/documents/DocumentsList";
import SignaturesTab from "@/components/signatures/SignaturesTab";
import { TaskActivityFeed } from "@/components/tasks/TaskActivityFeed";
import { Workflow, FileText, PenTool, Activity, Lightbulb } from "lucide-react";

interface WorkflowInstanceDetailModalProps {
  workflowInstance: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export const WorkflowInstanceDetailModal = ({
  workflowInstance,
  open,
  onOpenChange,
  onUpdate
}: WorkflowInstanceDetailModalProps) => {
  const [task, setTask] = useState<any>(null);
  const [workflow, setWorkflow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [displayStatus, setDisplayStatus] = useState(workflowInstance?.status || 'in_progress');
  const [displayProgress, setDisplayProgress] = useState(workflowInstance?.progress_percentage || 0);

  useEffect(() => {
    if (open && workflowInstance) {
      // Reset state before loading to ensure fresh data
      setLoading(true);
      setTask(null);
      setWorkflow(null);
      loadWorkflowDetails();
    }
  }, [open, workflowInstance?.task_id]);

  const loadWorkflowDetails = async () => {
    try {
      setLoading(true);

      // Load task details
      const { data: taskData, error: taskError } = await supabase
        .from('custom_tasks')
        .select('*')
        .eq('id', workflowInstance.task_id)
        .single();

      if (taskError) throw taskError;

      // Load workflow details
      const { data: workflowData, error: workflowError } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', workflowInstance.workflow_id)
        .single();

      if (workflowError) throw workflowError;

      // Load task phases to calculate fresh progress
      const { data: phases } = await supabase
        .from('task_phases')
        .select('status')
        .eq('task_id', workflowInstance.task_id);

      const totalPhases = phases?.length || 0;
      const completedPhases = phases?.filter(p => p.status === 'completed').length || 0;
      const freshProgress = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0;

      // Update display values from fresh data
      setDisplayStatus(taskData.status === 'completed' ? 'completed' : (taskData.status || 'in_progress'));
      setDisplayProgress(freshProgress);

      setTask(taskData);
      setWorkflow(workflowData);
    } catch (error: any) {
      console.error('Error loading workflow details:', error);
      toast.error("Failed to load workflow details");
    } finally {
      setLoading(false);
    }
  };

  if (!workflowInstance) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-500';
      case 'completed':
        return 'bg-green-500/10 text-green-500';
      case 'abandoned':
        return 'bg-gray-500/10 text-gray-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] md:w-full p-4 md:p-6">
        <DialogHeader>
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <DialogTitle className="text-xl md:text-2xl break-words">{workflowInstance.workflow_title}</DialogTitle>
                <Badge className={getStatusColor(displayStatus)}>
                  {displayStatus.replace('_', ' ')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 md:line-clamp-none">
                {workflowInstance.workflow_description}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Progress Overview */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{displayProgress}%</span>
          </div>
          <Progress value={displayProgress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            Phase {workflowInstance.current_phase} of {workflowInstance.total_phases}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="workflow" className="w-full">
            {/* Horizontally scrollable tabs on mobile */}
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <TabsList className="inline-flex w-auto md:grid md:w-full md:grid-cols-5 min-w-max md:min-w-0">
                <TabsTrigger value="workflow" className="gap-1 min-w-[44px] min-h-[44px] px-3">
                  <Workflow className="h-4 w-4" />
                  <span className="hidden sm:inline">Workflow</span>
                </TabsTrigger>
                <TabsTrigger value="guidance" className="gap-1 min-w-[44px] min-h-[44px] px-3">
                  <Lightbulb className="h-4 w-4" />
                  <span className="hidden sm:inline">Guidance</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className="gap-1 min-w-[44px] min-h-[44px] px-3">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Documents</span>
                </TabsTrigger>
                <TabsTrigger value="signatures" className="gap-1 min-w-[44px] min-h-[44px] px-3">
                  <PenTool className="h-4 w-4" />
                  <span className="hidden sm:inline">Signatures</span>
                </TabsTrigger>
                <TabsTrigger value="activity" className="gap-1 min-w-[44px] min-h-[44px] px-3">
                  <Activity className="h-4 w-4" />
                  <span className="hidden sm:inline">Activity</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="workflow" className="space-y-4">
              {task && workflow && (
                <WorkflowPhaseManager
                  key={`${task.id}-${displayProgress}`}
                  taskId={task.id}
                  workflowId={workflow.id}
                  unaId={task.una_id || ''}
                  onPhaseComplete={() => {
                    loadWorkflowDetails();
                    onUpdate();
                  }}
                  onPhaseChange={() => {
                    loadWorkflowDetails();
                    onUpdate();
                  }}
                />
              )}
            </TabsContent>

            <TabsContent value="guidance" className="space-y-4">
              {workflow && task && (
                <WorkflowGuidancePanel
                  workflowId={workflow.id}
                  currentPhaseNumber={workflowInstance.current_phase}
                  taskId={task.id}
                  unaId={task.una_id || ''}
                />
              )}
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              {task && (
                <DocumentsList
                  taskId={task.id}
                  unaId={task.una_id}
                />
              )}
            </TabsContent>

            <TabsContent value="signatures" className="space-y-4">
              {task && (
                <SignaturesTab
                  taskId={task.id}
                  unaId={task.una_id || ''}
                />
              )}
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              {task && (
                <TaskActivityFeed
                  taskId={task.id}
                  unaId={task.una_id || ''}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
